'use client';

import { SEARCH_TYPE } from '@/constants/searchType';
import { useState, useRef, useMemo, useEffect } from 'react';
import {
  Box,
  Button,
  IconButton,
  Popover,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Tooltip,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LinkIcon from '@mui/icons-material/Link';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import { SavedFilter } from '@/publicSearch/store/useSavedFiltersStore';
import useFilterStore from '@/publicSearch/store/useFilterStore';
import useSearchStore from '@/publicSearch/store/useSearchStore';
import useGridStore from '@/publicSearch/store/useGridStore';
import usePublicSearchContext from '@/publicSearch/store/usePublicSearchContext';
import { useSearchType } from '@/hooks/publicSearch/useSearchType';
import {
  FieldOption,
  LogicalOperator,
} from '@/components/publicSearch/constants';
import {
  buildEbvFieldOptions,
  EbvConfigItem,
} from '@/components/publicSearch/search/constants';
import { formatDistanceToNow } from 'date-fns';
import { parseEbvConfig } from '@/hooks/publicSearch/ebv/ebvColumns';
import { useIsEbvEnabled } from '@/hooks/publicSearch/useIsEbvEnabled';
import { isAnyEbvField } from '@/components/publicSearch/search/ebvFilterFields';
import { setSearchParam } from '@/components/entity/helpers';
import {
  SALE_DATE_FILTER_PARAM,
  SALE_TYPE_FILTER_PARAM,
} from '@/hooks/publicSearch/useCatalogueFilters';
import { publicSearchEndAdornmentHeightPx } from '@/components/publicSearch/search/constants';
import { useSearch } from '@/hooks/publicSearch/useSearch';
import { useSavedFilters } from '@/hooks/publicSearch/useSavedFilters';
import { useShareSearch } from '@/hooks/publicSearch/useShareSearch';
import { useSnackbar } from 'notistack';
import { SNACKBAR_VARIANT } from '@/constants/notifications';
import { SALE_DATE_FILTER } from '@/constants/saleFilters';

interface SavedFiltersDropdownProps {
  containerRef?: React.RefObject<HTMLElement | null>;
  compact?: boolean;
}

function savedFilterIsEbvOnly(filter: SavedFilter): boolean {
  const flat: { field?: string | null }[] = [...filter.filters];
  for (const g of filter.filterGroups ?? []) {
    for (const f of g.filters) {
      flat.push(f);
    }
  }
  const withField = flat.filter((f) => f.field && String(f.field).trim());
  if (withField.length === 0) return false;
  return withField.every((f) => isAnyEbvField(String(f.field)));
}

export default function SavedFiltersDropdown({
  containerRef,
  compact = false,
}: SavedFiltersDropdownProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [renameAnchorEl, setRenameAnchorEl] = useState<HTMLElement | null>(
    null,
  );
  const [renameValue, setRenameValue] = useState<string>('');
  const [renamingFilter, setRenamingFilter] = useState<SavedFilter | null>(
    null,
  );
  const [dropdownWidth, setDropdownWidth] = useState<number>(400);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const searchType = useSearchType();
  const {
    setFilters,
    updateCommonFilterOperator,
    clearFilterGroups,
    setFilterGroups,
    setGroupsOperator,
  } = useFilterStore(searchType);
  const {
    setBasicSearch,
    setOpenResult,
    setIsAiMode,
    setAiSearchPrompt,
    setSearchDisplayTerm,
  } = useSearchStore();
  const { paginationModel, setPaginationModel, setGridSortModel } =
    useGridStore();
  const {
    savedFilters,
    deleteFilter,
    clearAllFilters,
    updateFilter,
    isLoading,
  } = useSavedFilters();
  const society = usePublicSearchContext((context) => context.society);
  const isEbvEnabled = useIsEbvEnabled();
  const { startSearch } = useSearch();
  const { shareSavedFilter, isSharing } = useShareSearch();
  const { enqueueSnackbar } = useSnackbar();

  // Parse EBV config to get human-readable labels for EBV fields
  const ebvConfig = useMemo<EbvConfigItem[]>(() => {
    return parseEbvConfig(society.settings);
  }, [society.settings]);

  const effectiveEbvConfig = useMemo(
    () => (isEbvEnabled ? ebvConfig : []),
    [isEbvEnabled, ebvConfig],
  );

  const visibleSavedFilters = useMemo(() => {
    if (isEbvEnabled) return savedFilters;
    return savedFilters.filter((f) => !savedFilterIsEbvOnly(f));
  }, [isEbvEnabled, savedFilters]);

  // Build a map of field values to their human-readable labels
  const fieldLabelMap = useMemo(() => {
    const map = new Map<string, string>();

    // Add standard field labels
    Object.values(FieldOption).forEach((field) => {
      if (field.value && field.label) {
        map.set(field.value, field.label);
      }
    });

    // Add EBV field labels from the society's EBV config
    const ebvOptions = buildEbvFieldOptions(effectiveEbvConfig);
    ebvOptions.forEach((option) => {
      map.set(option.value, option.label);
    });

    return map;
  }, [effectiveEbvConfig]);

  // Helper to get human-readable label for a field value
  const getFieldLabel = (fieldValue: string): string => {
    return fieldLabelMap.get(fieldValue) || fieldValue;
  };

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleApplyFilter = async (filter: SavedFilter) => {
    // Reset pagination first
    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
    if (searchType === SEARCH_TYPE.SALE_CATALOGUES) {
      setSearchParam(
        SALE_DATE_FILTER_PARAM,
        filter.saleDateFilter === SALE_DATE_FILTER.ALL
          ? SALE_DATE_FILTER.ALL
          : SALE_DATE_FILTER.CURRENT,
      );
      setSearchParam(
        SALE_TYPE_FILTER_PARAM,
        filter.saleTypeFilterValues?.length
          ? filter.saleTypeFilterValues.join(',')
          : undefined,
      );
    }
    clearFilterGroups();
    const hasGrouped =
      (filter.filterGroups?.length ?? 0) > 0 &&
      filter.filterGroups!.some((g) => g.filters.length > 0);
    if (hasGrouped) {
      setFilterGroups(filter.filterGroups ?? []);
      setGroupsOperator(
        filter.groupsOperator === LogicalOperator.and
          ? LogicalOperator.and
          : LogicalOperator.or,
      );
      setIsAiMode(false);
      setAiSearchPrompt('');
      setSearchDisplayTerm(filter.searchTerm ?? '');
    } else {
      const isAi = filter.isAiMode ?? false;
      setIsAiMode(isAi);
      if (isAi) {
        setAiSearchPrompt(filter.aiSearchPrompt ?? '');
        setSearchDisplayTerm(filter.aiSearchPrompt || filter.searchTerm || '');
      } else {
        setAiSearchPrompt('');
        if (filter.searchTerm) {
          setBasicSearch(filter.searchTerm);
        } else {
          setSearchDisplayTerm('');
        }
      }
    }

    setFilters(filter.filters);
    updateCommonFilterOperator(LogicalOperator.and);
    setGridSortModel(filter.gridSortModel ?? []);
    setOpenResult(true);
    setTimeout(startSearch, 10);
    handleClose();
  };

  const handleShareFilter = async (
    e: React.MouseEvent,
    filter: SavedFilter,
  ) => {
    e.stopPropagation();
    const savedHasGrouped =
      (filter.filterGroups?.length ?? 0) > 0 &&
      filter.filterGroups!.some((g) => g.filters.length > 0);
    const isAi = savedHasGrouped ? false : (filter.isAiMode ?? false);
    const resolveSearchTerm = () => {
      if (savedHasGrouped) return '';
      if (isAi) return filter.aiSearchPrompt || filter.searchTerm;
      return filter.searchTerm;
    };
    await shareSavedFilter({
      searchType: filter.searchType ?? searchType,
      filters: filter.filters,
      commonFilterOperator: LogicalOperator.and,
      searchTerm: resolveSearchTerm(),
      gridSortModel: filter.gridSortModel ?? [],
      isAiMode: isAi,
      filterGroups: filter.filterGroups ?? [],
      groupsOperator: filter.groupsOperator ?? LogicalOperator.or,
    });
  };

  const handleDeleteFilter = async (e: React.MouseEvent, filterId: string) => {
    e.stopPropagation();
    try {
      await deleteFilter(filterId);
      enqueueSnackbar('Filter deleted', {
        variant: SNACKBAR_VARIANT.SUCCESS,
        autoHideDuration: 2000,
      });
    } catch {
      enqueueSnackbar('Failed to delete filter', {
        variant: SNACKBAR_VARIANT.ERROR,
        autoHideDuration: 3000,
      });
    }
  };

  const handleClearAll = async () => {
    try {
      await clearAllFilters();
      enqueueSnackbar('All filters cleared', {
        variant: SNACKBAR_VARIANT.SUCCESS,
        autoHideDuration: 2000,
      });
      handleClose();
    } catch {
      enqueueSnackbar('Failed to clear filters', {
        variant: SNACKBAR_VARIANT.ERROR,
        autoHideDuration: 3000,
      });
    }
  };

  const handleOpenRename = (
    e: React.MouseEvent<HTMLElement>,
    filter: SavedFilter,
  ) => {
    e.stopPropagation();
    setRenamingFilter(filter);
    setRenameValue(filter.name);
    setRenameAnchorEl(e.currentTarget);
  };

  const handleCloseRename = () => {
    setRenameAnchorEl(null);
    setRenamingFilter(null);
    setRenameValue('');
  };

  const handleRenameFilter = async () => {
    if (!renamingFilter) return;

    try {
      await updateFilter(renamingFilter.id, {
        searchType,
        name: renameValue.trim() || renamingFilter.name,
        filters: renamingFilter.filters,
        searchTerm: renamingFilter.searchTerm,
        saleDateFilter: renamingFilter.saleDateFilter ?? null,
        saleTypeFilterValues: renamingFilter.saleTypeFilterValues ?? [],
        gridSortModel: renamingFilter.gridSortModel ?? [],
        isAiMode: renamingFilter.isAiMode ?? false,
        filterGroups: renamingFilter.filterGroups ?? [],
        groupsOperator: renamingFilter.groupsOperator ?? LogicalOperator.or,
        aiSearchPrompt: renamingFilter.aiSearchPrompt ?? '',
      });
      enqueueSnackbar('Filter renamed', {
        variant: SNACKBAR_VARIANT.SUCCESS,
        autoHideDuration: 2000,
      });
      handleCloseRename();
    } catch {
      enqueueSnackbar('Failed to rename filter', {
        variant: SNACKBAR_VARIANT.ERROR,
        autoHideDuration: 3000,
      });
    }
  };

  useEffect(() => {
    if (containerRef?.current) {
      setDropdownWidth(containerRef.current.offsetWidth);
    }
  }, [containerRef, open]);

  const formatFilterSummary = (filter: SavedFilter) => {
    const parts: string[] = [];
    if (filter.searchTerm) {
      parts.push(`Search: "${filter.searchTerm}"`);
    }

    const conditionLabels: string[] = [];
    for (const f of filter.filters) {
      if (f.field && f.value) {
        conditionLabels.push(`${getFieldLabel(f.field)}: ${f.value}`);
      }
    }
    for (const g of filter.filterGroups ?? []) {
      for (const f of g.filters) {
        if (f.field && f.value) {
          conditionLabels.push(`${getFieldLabel(f.field)}: ${f.value}`);
        }
      }
    }

    const maxShown = 3;
    conditionLabels.slice(0, maxShown).forEach((label) => parts.push(label));
    if (conditionLabels.length > maxShown) {
      parts.push(`+${conditionLabels.length - maxShown} more`);
    }

    return parts.join(' • ') || 'No filters';
  };

  return (
    <>
      <Tooltip title="Load saved filter" placement="bottom">
        <IconButton
          ref={buttonRef}
          onClick={handleClick}
          sx={{
            boxSizing: 'border-box',
            height: publicSearchEndAdornmentHeightPx,
            minHeight: publicSearchEndAdornmentHeightPx,
            maxHeight: publicSearchEndAdornmentHeightPx,
            borderRadius: '0 4px 4px 0',
            backgroundColor: 'info.main',
            color: 'info.contrastText',
            minWidth: { xs: 22, sm: 28 },
            width: { xs: 22, sm: 'auto' },
            px: 0,
            py: 0,
            marginRight: 0.5,
            '& .MuiSvgIcon-root': {
              fontSize: { xs: 18, sm: 20 },
            },
            ...(compact && { ml: '-2px' }),
            '&:hover': {
              backgroundColor: 'info.dark',
            },
          }}
          aria-label="Load saved filter"
        >
          <ArrowDropDownIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              mt: compact ? 0.5 : 1,
              width: compact ? 'min(92vw, 340px)' : dropdownWidth,
              maxWidth: compact ? 'calc(100vw - 16px)' : '100vw',
              maxHeight: 400,
              overflow: 'hidden',
            },
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: compact ? 1.25 : 2,
            py: compact ? 1 : 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            gap: 1,
          }}
        >
          <Typography
            variant="titlemedium"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: compact ? 0.5 : 1,
              minWidth: 0,
              fontSize: compact ? '0.95rem' : undefined,
            }}
          >
            <BookmarkIcon fontSize="small" color="primary" />
            Saved Filters
          </Typography>
          {visibleSavedFilters.length > 0 && (
            <Button
              size="small"
              onClick={() => void handleClearAll()}
              sx={{
                textTransform: 'none',
                color: 'text.secondary',
                minWidth: compact ? 0 : undefined,
                px: compact ? 0.5 : undefined,
                whiteSpace: 'nowrap',
              }}
              disabled={isLoading}
            >
              Clear All
            </Button>
          )}
        </Box>

        {isLoading && visibleSavedFilters.length === 0 && (
          <Box sx={{ p: compact ? 2 : 3, textAlign: 'center' }}>
            <Typography
              variant="bodysmall"
              sx={{
                color: 'text.secondary',
              }}
            >
              Loading saved filters...
            </Typography>
          </Box>
        )}
        {!isLoading && visibleSavedFilters.length === 0 && (
          <Box sx={{ p: compact ? 2 : 3, textAlign: 'center' }}>
            <Typography
              variant="bodysmall"
              sx={{
                color: 'text.secondary',
              }}
            >
              No saved filters yet.
            </Typography>
            <Typography
              variant="labelsmall"
              sx={{
                color: 'text.secondary',
              }}
            >
              Use the save button to save your current filter.
            </Typography>
          </Box>
        )}
        {visibleSavedFilters.length > 0 && (
          <List
            sx={{
              py: 0,
              maxHeight: compact ? 280 : 320,
              overflow: 'auto',
            }}
          >
            {visibleSavedFilters.map((filter) => (
              <ListItem
                key={filter.id}
                disablePadding
                secondaryAction={
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mr: compact ? 0 : 0.5,
                      gap: compact ? 0.25 : 0,
                    }}
                  >
                    <Tooltip title="Share filter link" placement="top">
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={(e) => void handleShareFilter(e, filter)}
                        disabled={isLoading || isSharing}
                      >
                        <LinkIcon
                          sx={{
                            fontSize: 'small',
                          }}
                        />
                      </IconButton>
                    </Tooltip>
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={(e) => handleOpenRename(e, filter)}
                      disabled={isLoading}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={(e) => void handleDeleteFilter(e, filter.id)}
                      disabled={isLoading}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                }
              >
                <ListItemButton
                  onClick={() => void handleApplyFilter(filter)}
                  sx={{
                    py: compact ? 0.75 : 1,
                    px: compact ? 1.25 : 2,
                    pr: compact ? 8.5 : undefined,
                  }}
                >
                  <ListItemText
                    primary={
                      filter.name.startsWith('Saved Filter ')
                        ? formatFilterSummary(filter)
                        : filter.name
                    }
                    secondary={
                      <Box
                        component="span"
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.25,
                        }}
                      >
                        {!filter.name.startsWith('Saved Filter ') && (
                          <Typography
                            component="span"
                            variant="labelsmall"
                            sx={{
                              color: 'text.secondary',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              display: 'block',
                            }}
                          >
                            {formatFilterSummary(filter)}
                          </Typography>
                        )}
                        <Typography
                          component="span"
                          variant="labelsmall"
                          sx={{
                            color: 'text.disabled',
                            display: 'block',
                          }}
                        >
                          {formatDistanceToNow(filter.createdAt, {
                            addSuffix: true,
                          })}
                        </Typography>
                      </Box>
                    }
                    slotProps={{
                      primary: {
                        component: 'span',
                        variant: 'bodysmall',
                        sx: {
                          fontWeight: 500,
                          pr: compact ? 0.5 : 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        },
                      },

                      secondary: {
                        component: 'div',
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </Popover>
      <Popover
        open={Boolean(renameAnchorEl)}
        anchorEl={renameAnchorEl}
        onClose={handleCloseRename}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              p: 2,
              mt: 1,
              minWidth: 260,
            },
          },
        }}
      >
        <Typography variant="titlemedium" sx={{ mb: 1.5 }}>
          Rename saved filter
        </Typography>
        <TextField
          fullWidth
          size="small"
          value={renameValue}
          onChange={(e) => setRenameValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              void handleRenameFilter();
            } else if (e.key === 'Escape') {
              handleCloseRename();
            }
          }}
          sx={{ mb: 2 }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button size="small" onClick={handleCloseRename}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={() => void handleRenameFilter()}
            disabled={isLoading}
          >
            Save
          </Button>
        </Box>
      </Popover>
    </>
  );
}
